import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { Operation, SharesTitle } from 'types';

import operationTypes from 'constants/operationTypes';

import { getPartnerOperations } from 'modules/partners/api/getPartnerOperations';
import { getSharesTitlesByPartnerOperations } from 'modules/partners/api/getSharesTitlesByPartnerOperations';

type UseGetPartnerOperationsProps = {
  partnerId: string | undefined;
  page: number;
  rowsPerPage: number;
};

export const excludedOperations = [
  operationTypes.ADD_PARTNER,
  operationTypes.ADD_PARTNERS,
  operationTypes.ADD_BENEFICIARY,
  operationTypes.STOCK_OPTION,
  operationTypes.PHANTOM_SHARE,
];

export const useGetPartnerOperations = ({
  partnerId,
  page,
  rowsPerPage,
}: UseGetPartnerOperationsProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const [operations, setOperations] = useState([]);
  const [totalOperations, setTotalOperations] = useState(0);
  const [sharesTitles, setSharesTitles] = useState<SharesTitle[]>([]);

  const fetchPartnerOperations = useCallback(
    async (controller: AbortController) => {
      if (!partnerId) return;

      try {
        setIsLoading(true);

        const operationsData = await store.dispatch(
          getPartnerOperations({
            partnerId,
            params: {
              page,
              items: rowsPerPage,
              fieldsToPopulate: ['transactions.shareClass', 'documents'],
            },
            signal: controller.signal,
          })
        );

        const { operations, total } = operationsData;

        setOperations(operations);
        setTotalOperations(total);

        const operationIds =
          operations?.map((operation: Operation) => operation._id) || [];

        const operationShareTitles = await store.dispatch(
          getSharesTitlesByPartnerOperations(partnerId, operationIds)
        );
        setSharesTitles(operationShareTitles);

        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [partnerId, page, rowsPerPage]
  );

  useEffect(() => {
    const controller = new AbortController();

    fetchPartnerOperations(controller);

    return () => controller.abort();
  }, [fetchPartnerOperations]);

  return {
    data: { operations, totalOperations, sharesTitles },
    isLoading,
  };
};
